import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { DocumentPageModel, DocumentPagesModel, DocumentPagesRequestModel, DocumentsService } from "src/common";
import { TranslationManager } from "src/common/translation";

export class DocumentViewModel {
    public documentId: number;
    public base64File: string;
    public selectedPages: string;
    public disabledPages: string;
}

export class DocumentViewItem {
    public uid: string;
    public fileSource: string;
    public pageNumber: number;
    public selected: boolean;
    public disabled: boolean;
    public rendered: boolean;
}

@Component({
    selector: 'document-view',
    styleUrls: ['./document-view.component.scss'],
    templateUrl: './document-view.component.html'
})
export class DocumentViewComponent {

    constructor(private documentsService: DocumentsService) { }

    translationManager = TranslationManager;
    @ViewChild("pdfPages") pdfPagesElement: ElementRef;

    items: DocumentViewItem[] = [];
    multiSelectPageFrom: string;
    multiSelectPageTo: string;
    zoomScale: number = 100;
    numberOfPages: number = 0;
    private _zoomScaleMin: number = 30;
    private _zoomScaleMax: number = 150;
    private _zoomStep: number = 10;

    private _selectable: boolean = false;
    private _pageCount: boolean = false;
    private _zoom: boolean = false;
    private _multiSelect: boolean = false;
    private _showSelectedPagesOnly: boolean = false;

    private _selectedPages: string;
    private _disabledPages: string;
    private _documentId: number;
    private timeout: any;

    @Input() set selectedPages(val: string) {
        this._selectedPages = val;
        this.selectPages(val);
        this.getPdfPagesAsImageByVisibility();
        if (this.selectedPagesChange) {
            this.selectedPagesChange.emit(this._selectedPages);
        }
    }
    get selectedPages() {
        return this._selectedPages;
    }
    @Output() selectedPagesChange: EventEmitter<string> = new EventEmitter<string>();

    @Input() set disabledPages(val: string) {
        this._disabledPages = val;
        this.disablePages();
        if (this.disabledPagesChange) {
            this.disabledPagesChange.emit(this._disabledPages);
        }
    }
    get disabledPages() {
        return this._disabledPages;
    }
    @Output() disabledPagesChange: EventEmitter<string> = new EventEmitter<string>();

    @Input() set selectable(value: boolean) {
        this._selectable = value;
    }
    get selectable() {
        return this._selectable;
    }

    @Input() set multiSelect(value: boolean) {
        this._multiSelect = value;
    }
    get multiSelect() {
        return this._multiSelect;
    }

    @Input() set showSelectedPagesOnly(value: boolean) {
        this._showSelectedPagesOnly = value;
    }
    get showSelectedPagesOnly() {
        return this._showSelectedPagesOnly;
    }

    @Input() set pageCount(value: boolean) {
        this._pageCount = value;
    }
    get pageCount() {
        return this._pageCount;
    }

    @Input() set zoom(value: boolean) {
        this._zoom = value;
    }
    get zoom() {
        return this._zoom;
    }

    @Input() set model(model: DocumentViewModel) {
        if (model) {
            this._documentId = model.documentId;
            this.documentsService.getDocumentPagesCount(model.documentId).then((count: number) => {

                this.items.splice(0, this.items.length);

                for (let page = 1; page <= count; page++) {
                    let item: DocumentViewItem = new DocumentViewItem();
                    item.pageNumber = page;
                    this.items.push(item);
                }

                this.getPdfPagesAsImageByVisibility();
                this.disablePages();
                this.setNumberOfPages();
            })

            this.selectedPages = model.selectedPages;
            this.disabledPages = model.disabledPages;
        }
    }

    private getPdfPagesAsImageByVisibility() {

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
            let request: DocumentPagesRequestModel = new DocumentPagesRequestModel();

            request.documentId = this._documentId;
            request.pages = [];

            let pdfItems = this.pdfPagesElement.nativeElement.querySelectorAll("div.pdf-item") as Element[];
            pdfItems.forEach((e: Element) => {
                if (this.isPageInContainer(e) && this.isPageVisible(e) && !this.isPageRendered(e)) {
                    request.pages.push(parseInt(e.attributes['page'].value))
                }
            })

            if (request.pages.length > 0) {
                this.documentsService.getDocumentPages(request).then((documentPages: DocumentPagesModel) => {
                    if (documentPages) {
                        if (documentPages.pages) {
                            for (let page = 0; page < documentPages.pages.length; page++) {
                                let documentPage: DocumentPageModel = documentPages.pages[page];
                                let documentItem = this.items.filter(s => s.pageNumber == documentPage.page)[0];

                                if (documentItem) {
                                    documentItem.fileSource = `data:image/png;base64,${documentPage.content}`;
                                    documentItem.rendered = true;
                                }
                            }
                        }
                    }
                })
            }
        }, 500);
    }

    private selectPages(selectedPages: string) {
        let pages = selectedPages ? selectedPages.split(',') : [];

        if (this.items) {
            this.items.forEach((item: DocumentViewItem) => {
                item.selected = undefined;
                item.selected = pages.filter(s => item.pageNumber.toString() === s).length > 0;
            });
        }

        this.setNumberOfPages();
    }

    private disablePages() {
        let pages = this._disabledPages ? this._disabledPages.split(',') : [];

        if (this.items) {
            this.items.forEach((item: DocumentViewItem) => {
                item.disabled = undefined;
                item.disabled = pages.filter(s => item.pageNumber.toString() === s).length > 0;
            });
        }
    }

    private renderPage(page: any, canvas: HTMLCanvasElement) {
        var viewport = page.getViewport({ scale: 4 });

        var context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        var renderContext = {
            canvasContext: context,
            viewport: viewport
        };

        page.render(renderContext);
    }

    zoomIn() {
        let zoom: number = this.zoomScale;
        zoom = zoom + this._zoomStep;
        this.zoomScale = zoom > this._zoomScaleMax ? this._zoomScaleMax : zoom;

        this.getPdfPagesAsImageByVisibility();
    }

    zoomOut() {
        let zoom: number = this.zoomScale;
        zoom = zoom - this._zoomStep;
        this.zoomScale = zoom < this._zoomScaleMin ? this._zoomScaleMin : zoom;

        this.getPdfPagesAsImageByVisibility();
    }

    select(item: DocumentViewItem) {
        item.selected = !item.selected;
        this.selectedPages = this.getSelectedItems();
    }

    private getSelectedItems(): string {
        let selectedItems = [];

        if (this.items) {
            this.items.filter(s => s.selected).forEach(s => {
                selectedItems.push(s.pageNumber);
            })
        }

        return selectedItems.join(',');
    }

    multiSelectPages() {
        if (this.multiSelectPageFrom && this.multiSelectPageTo && this.items) {
            let pageFrom = parseInt(this.multiSelectPageFrom);
            let pageTo = parseInt(this.multiSelectPageTo);

            if (!isNaN(pageFrom) && !isNaN(pageTo)) {
                pageFrom = pageFrom < 1 ? 1 : pageFrom;
                pageFrom = pageFrom > this.items.length ? this.items.length : pageFrom;

                pageTo = pageTo < 1 ? 1 : pageTo;
                pageTo = pageTo > this.items.length ? this.items.length : pageTo;

                if (pageFrom > 0 && pageTo > 0 && pageTo >= pageFrom) {
                    for (let page = pageFrom; page <= pageTo; page++) {
                        let pageItem = this.items[page-1];
                        if (!pageItem.disabled) {
                            pageItem.selected = true;
                        }
                    }

                    this.selectedPages = this.getSelectedItems();
                }
            }
        }
    }

    private setNumberOfPages() {
        if (this.items) {
            this.numberOfPages = this.showSelectedPagesOnly ? this.items.filter(s => s.selected).length : this.items.length;
        }
    }

    onScroll() {
        this.getPdfPagesAsImageByVisibility();
    }

    isPageVisible(page: any) {
        var pageTop = this.pdfPagesElement.nativeElement.scrollTop;
        var pageBottom = pageTop + this.pdfPagesElement.nativeElement.offsetHeight;
        var elementTop = page.offsetTop;
        var elementBottom = elementTop + page.offsetHeight;

        return ((elementTop <= pageBottom) && (elementBottom >= pageTop));
    }

    isPageRendered(page: any) {
        return page.attributes['rendered']?.value == 'true';
    }

    isPageInContainer(page: any) {
        return page.attributes['in-container']?.value == 'true';
    }
}
